import React from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { nanoid } from 'nanoid';
import ColumnName from '@/util/ColumnName';
import { DraggableSelect } from './subcomponents/DraggableSelect';
import { type ConfigurationSelectProps } from './subcomponents/ConfigurationSelect';

const SelectsContainer = styled.div`
  flex-grow: 1;
`;

export type ConfigurationProps<T> = {
  configurableType: 'metrics' | 'dimensions';
  selectedOptions: T[];
  optionsCombinations: T[][];
  onOptionSelect: ConfigurationSelectProps<T>['onOptionSelect'];
  onSelectRemove: ConfigurationSelectProps<T>['onSelectRemove'];
  onReorderSelects: F2<Index, Index>;
  onAddSelect?: F0;
  min: number;
};
export const Configuration: React.FC<ConfigurationProps<ColumnName>> = ({
  configurableType,
  selectedOptions,
  optionsCombinations,
  onSelectRemove,
  onOptionSelect,
  onReorderSelects,
  min,
}) => {
  const draggableIds = React.useMemo(() => {
    return optionsCombinations.map(() => nanoid());
  }, [JSON.stringify(optionsCombinations)]);
  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (destination) {
      onReorderSelects(source.index, destination.index);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={`${configurableType}-order-droppable`}>
        {(providedDroppable) => (
          <SelectsContainer
            {...providedDroppable.droppableProps}
            ref={providedDroppable.innerRef}
          >
            {optionsCombinations.map((options, index) => {
              return (
                <DraggableSelect
                  key={draggableIds[index]}
                  id={draggableIds[index]}
                  index={index}
                  onSelectRemove={onSelectRemove}
                  options={options}
                  selectedOption={selectedOptions[index]}
                  onOptionSelect={onOptionSelect}
                  draggable={optionsCombinations.length > 1}
                  isRequired={index < min}
                />
              );
            })}
            {providedDroppable.placeholder}
          </SelectsContainer>
        )}
      </Droppable>
    </DragDropContext>
  );
};
